import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pickaxe, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiFetch } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { CharacterCard } from '@/components/game-ui/CharacterCard';
import { GAME_TEMPLATES } from '@/lib/game-logic/templates';
import type { GameTemplate, CharacterArchetype } from '@/lib/game-logic/templates';

const TEMPLATE_ICONS: Record<string, React.ElementType> = {
  Pickaxe,
};

export function NewGame() {
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState<GameTemplate | null>(null);
  const [selectedArchetype, setSelectedArchetype] = useState<CharacterArchetype | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!selectedTemplate || !selectedArchetype) return;

    setLoading(true);
    setError(null);

    try {
      const { settings } = selectedTemplate;
      const response = await apiFetch('/api/games', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          region: settings.region,
          theme: settings.difficulty,
          language: settings.language,
          adventureTheme: settings.adventureTheme,
          maxPlayers: settings.maxPlayers,
          character: {
            name: selectedArchetype.name,
            race: selectedArchetype.race,
            kit: selectedArchetype.kit,
            magic: selectedArchetype.magic,
            stats: selectedArchetype.stats,
            equipment: selectedArchetype.equipment,
            origin: 'The Roots',
            callForAdventure: 'The ancient ruins beneath Vannar whisper of forgotten treasures.',
          },
        }),
      });

      const { game } = await response.json();
      navigate(`/game/${game.id}/play`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <PageHeader title="New Game" backHref="/games" />

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Template Selection */}
        <section>
          <h2 className="text-sm font-medium text-neutral-400 mb-3">Choose a template</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {GAME_TEMPLATES.map((template) => {
              const Icon = TEMPLATE_ICONS[template.icon] || Pickaxe;
              const isSelected = selectedTemplate?.id === template.id;
              return (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => {
                    setSelectedTemplate(template);
                    setSelectedArchetype(null);
                  }}
                  className={cn(
                    'p-4 rounded-lg border text-left transition-colors flex items-start gap-3',
                    isSelected
                      ? 'bg-white/5 border-amber-500/40 ring-1 ring-amber-500/20'
                      : 'bg-neutral-900 border-neutral-800 text-neutral-400 hover:border-neutral-700'
                  )}
                >
                  <Icon className={cn('w-5 h-5 mt-0.5 flex-shrink-0', isSelected ? 'text-amber-400' : 'text-neutral-500')} />
                  <div>
                    <div className="font-medium text-sm text-white">{template.name}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{template.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Character Selection */}
        {selectedTemplate && (
          <section>
            <h2 className="text-sm font-medium text-neutral-400 mb-3">Choose your character</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              {selectedTemplate.archetypes.map((archetype) => (
                <CharacterCard
                  key={archetype.id}
                  archetype={archetype}
                  selected={selectedArchetype?.id === archetype.id}
                  onClick={() => setSelectedArchetype(archetype)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Begin Button */}
        <div className="pt-2">
          <button
            onClick={handleSubmit}
            disabled={!selectedTemplate || !selectedArchetype || loading}
            className={cn(
              'w-full py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2',
              !selectedTemplate || !selectedArchetype || loading
                ? 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
                : 'bg-white text-black hover:bg-neutral-200'
            )}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Forging your tale...' : 'Begin Adventure'}
          </button>
        </div>
      </main>
    </div>
  );
}
